/**
 * CalendarEventBlock — renders a single event in the calendar.
 * Three styles: block (filled), dot (indicator), multi-day bar.
 * Used in month, week, and day views.
 */
import React from 'react';
import { resolveEventColor, type CalendarEvent } from './calendarEventPositioning';
import { pmTheme } from './pmTheme';

interface CalendarEventBlockProps {
  event: CalendarEvent;
  colorMap?: Record<string, string>;
  showTime?: boolean;
  isMultiDay?: boolean;
  isCompact?: boolean;
  onClick?: (event: CalendarEvent) => void;
  onMouseEnter?: (event: React.MouseEvent, calEvent: CalendarEvent) => void;
  onMouseLeave?: () => void;
  style?: React.CSSProperties;
}

export const CalendarEventBlock: React.FC<CalendarEventBlockProps> = React.memo(({
  event,
  colorMap,
  showTime = true,
  isMultiDay = false,
  isCompact = false,
  onClick,
  onMouseEnter,
  onMouseLeave,
  style,
}) => {
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick?.(event);
    }
  };
  const color = resolveEventColor(event.category, colorMap);
  const title = event.title || '(No title)';

  const formatTime = (dateStr: string): string => {
    if (!dateStr.includes('T')) return '';
    try {
      const d = new Date(dateStr);
      const h = d.getHours();
      const m = d.getMinutes();
      const ampm = h >= 12 ? 'PM' : 'AM';
      const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
      return m === 0 ? `${hour}${ampm}` : `${hour}:${String(m).padStart(2, '0')}${ampm}`;
    } catch {
      return '';
    }
  };

  const timeStr = showTime ? formatTime(event.start) : '';

  if (isCompact) {
    return (
      <div
        data-testid={`calendar-event-${event.id}`}
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          backgroundColor: color,
          display: 'inline-block',
          margin: '0 1px',
          ...style,
        }}
        title={title}
        role="button"
        tabIndex={0}
        aria-label={`Event: ${title}`}
        onClick={() => onClick?.(event)}
        onKeyDown={handleKeyDown}
      />
    );
  }

  return (
    <div
      data-testid={`calendar-event-${event.id}`}
      style={{
        backgroundColor: `${color}20`,
        borderLeft: isMultiDay ? 'none' : `3px solid ${color}`,
        borderRadius: isMultiDay ? '4px' : '2px',
        padding: '2px 4px',
        marginBottom: 1,
        cursor: 'pointer',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        fontSize: '11px',
        lineHeight: '16px',
        color: pmTheme.text,
        ...(isMultiDay && {
          backgroundColor: color,
          color: pmTheme.textInverse,
          borderRadius: '4px',
        }),
        ...style,
      }}
      role="button"
      tabIndex={0}
      aria-label={`Event: ${title}${timeStr ? ` at ${timeStr}` : ''}`}
      onClick={() => onClick?.(event)}
      onKeyDown={handleKeyDown}
      onMouseEnter={e => onMouseEnter?.(e, event)}
      onMouseLeave={onMouseLeave}
    >
      {timeStr && !isMultiDay && (
        <span style={{ fontWeight: 600, marginRight: 3, fontSize: '10px' }}>{timeStr}</span>
      )}
      <span>{title}</span>
    </div>
  );
});
